import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { InferType } from 'yup';

import { addCareNoteSchema } from '@views/pages/Patient/components/CareNotes/schemas';

import {
  useAddCareNoteMutation,
  useAllProgramListQuery,
  useEditCareNoteMutation,
  usePatientCareNoteQuery,
  usePatientQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';

type IFormShape = InferType<typeof addCareNoteSchema>;

export const ACTIVITY_MINUTES_KEYS = [
  'plan',
  'reconciliate',
  'coordinate',
  'educate',
  'communicate',
] as const;

const emptyActivityMinutes: NonNullable<IFormShape['activity_minutes']> = {
  plan: 0,
  reconciliate: 0,
  coordinate: 0,
  educate: 0,
  communicate: 0,
};

export const MAX_ACTIVITY_MINUTES = 1440;

const sumActivityMinutes = (
  activity_minutes: Partial<
    Record<(typeof ACTIVITY_MINUTES_KEYS)[number], number>
  >,
) =>
  ACTIVITY_MINUTES_KEYS.reduce(
    (acc, key) => acc + (Number(activity_minutes[key]) || 0),
    0,
  );

const initialValues: Partial<IFormShape> = {
  program_id: '',
  provider_id: '',
  notable_call: false,
  note: '',
  activity_minutes: { ...emptyActivityMinutes },
};

export const useAddCareNote = (
  patient_id: string,
  note_id: string,
  variant: 'create' | 'edit',
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync: addCareNotesMutate, isPending: addIsPending } =
    useAddCareNoteMutation(patient_id);
  const { mutateAsync: editCareNotesMutate, isPending: editIsPending } =
    useEditCareNoteMutation(patient_id, note_id);

  const { data: programOptions = [], isPending: isProgramPending } =
    useAllProgramListQuery();
  const { data } = usePatientCareNoteQuery(patient_id, note_id);
  const { data: patientData } = usePatientQuery({ patient_id });

  const notesTypeOptions = [
    { value: 'Email', label: t('common.notesTypeOptions.email') },
    { value: 'Phone call', label: t('common.notesTypeOptions.phoneCall') },
    { value: 'In person', label: t('common.notesTypeOptions.inPerson') },
    { value: 'Reflection', label: t('common.notesTypeOptions.reflection') },
    { value: 'Text', label: t('common.notesTypeOptions.text') },
    { value: 'Other', label: t('common.notesTypeOptions.other') },
  ];

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addCareNoteSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    reset,
  } = formMethods;

  const careManagementActivitiesEditable =
    variant === 'edit' && !!data?.care_management_activities_editable;

  const activityLabels: Record<(typeof ACTIVITY_MINUTES_KEYS)[number], string> =
    {
      plan: t(
        'worklist.worklistItem.requirements.otherActivities.carePlanUpdate',
      ),
      reconciliate: t(
        'worklist.worklistItem.requirements.otherActivities.medicationReconciliation',
      ),
      coordinate: t(
        'worklist.worklistItem.requirements.otherActivities.careCoordination',
      ),
      educate: t(
        'worklist.worklistItem.requirements.otherActivities.patientPoaEducation',
      ),
      communicate: t(
        'worklist.worklistItem.requirements.otherActivities.communicationForPatientRequests',
      ),
    };

  const handleActivityChange = (
    key: (typeof ACTIVITY_MINUTES_KEYS)[number],
    value: number,
  ) => {
    const current = {
      ...emptyActivityMinutes,
      ...(formMethods.getValues('activity_minutes') ?? {}),
    };
    const othersSum = ACTIVITY_MINUTES_KEYS.reduce<number>(
      (acc, k) => (k === key ? acc : acc + (Number(current[k]) || 0)),
      0,
    );
    const cappedValue = Math.max(
      0,
      Math.min(value, MAX_ACTIVITY_MINUTES - othersSum),
    );
    const next = {
      ...current,
      [key]: cappedValue,
    };
    formMethods.setValue('activity_minutes', next, {
      shouldDirty: true,
      shouldValidate: true,
    });
    const sum = ACTIVITY_MINUTES_KEYS.reduce<number>(
      (acc, k) => acc + (Number(next[k]) || 0),
      0,
    );
    formMethods.setValue('recorded_minutes', sum, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (variant === 'edit' && data) {
      const activity_minutes = data.activity_minutes
        ? { ...emptyActivityMinutes, ...data.activity_minutes }
        : { ...emptyActivityMinutes };

      reset({
        notable_call: data.notable_call,
        note_type: data.note_type,
        note: data.note,
        recorded_minutes: data.activity_minutes
          ? sumActivityMinutes(activity_minutes)
          : data.recorded_minutes,
        program_id: data.program_id,
        provider_id: data.provider_id,
        activity_minutes,
      });
    }
  }, [data]);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    const action =
      variant === 'edit' ? editCareNotesMutate : addCareNotesMutate;

    try {
      const activity_minutes = values.activity_minutes;
      const includeActivityMinutes =
        careManagementActivitiesEditable &&
        !!activity_minutes &&
        sumActivityMinutes(activity_minutes) >= 0;

      await action({
        note_type: values.note_type,
        note: values.note || '',
        notable_call: !!values.notable_call,
        ...(values.program_id && { program_id: values.program_id }),
        ...(values.provider_id && { provider_id: values.provider_id }),
        recorded_minutes: values.recorded_minutes,
        ...(includeActivityMinutes && { activity_minutes }),
      });
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
      toast.success('Changes saved!');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  const isCCMLimitWarningDisplayed =
    patientData?.programs.map(o => o.name).includes('CCM') &&
    (patientData.ccm_minutes_this_month ?? 0) >= 180;

  return {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    notesTypeOptions,
    isValid,
    formMethods,
    programOptions: programOptions.map(user => ({
      value: user.id,
      label: user.name,
    })),
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: addIsPending || editIsPending || isProgramPending,
    isCCMLimitWarningDisplayed,
    careManagementActivitiesEditable,
    activityLabels,
    handleActivityChange,
  };
};
